import { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  DataTable,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderActionsRightSection,
  DataTableHeaderColumnsMenu,
  DataTableHeaderRow,
  DataTableHeaderSavedViews,
  DataTablePagination,
  DataTableTable,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Flex,
  FormattedDateTime,
  FormattedRelativeTime,
  Heading,
  Link,
  Panel,
  PanelInset,
  Search,
  SegmentedControl,
  Text,
  TextLabel,
  Truncated,
  type DataTableColumnsSettingsConfig,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import type { ColumnDef, ColumnMeta } from '@tanstack/react-table';
import { useRouter } from '@tanstack/react-router';
import { objectIcon } from './object-type-header';
import type { ObjectRecord } from '@/services/db/object/entity';
import { objectName } from '@/services/db/object/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import { isEmptyString } from '@/utils/string';
import { formatNumberAndPluralize } from '@/utils/number';
import { generatePath } from '@/services/router/generate-path';
import type { DeleteObjectsFilter } from '@/services/db/object/mutations';
import { useDeleteObjectsModal } from './delete-objects-modal';
import { ObjectFilterBuilder, type ObjectFilterBuilderProps } from './object-filter-builder';

type ObjectColumnMeta = ColumnMeta<DataTableFeatures, ObjectRecord, unknown> & { _isPrivateColumn?: boolean };
const FIXED_COLUMN_META: ObjectColumnMeta = { _isPrivateColumn: true };

function isTimestamp(value: string): boolean {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 100_000_000 && numeric < 10_000_000_000;
}

type ObjectListTableProps = {
  objectType: ObjectType;
  objects: ObjectRecord[];
  total: number;
  page: number;
  name: string;
  filters: string | null;
  activeFilterBy: 'name' | 'conditions';
  withoutRelationships: boolean;
  withRelationships: boolean;
  message: string;
  isLoading: boolean;
  canConfigure: boolean;
  canDelete: boolean;
  shouldRedactCreatedAt: boolean;
  availableColumns: readonly string[];
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  columnsSettings: DataTableColumnsSettingsConfig;
  rowSelection: Record<string, boolean>;
  allSelected: boolean;
  allMatchingSelected: boolean;
  deleteFilters: DeleteObjectsFilter | null;
  onRowSelectionChange: (selection: Record<string, boolean>) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onSearch: (name: string) => void;
  onApplyFilters: ObjectFilterBuilderProps['onChange'];
  onActivateFilterBy: (value: 'name' | 'conditions') => void;
  onPageChange: (page: number) => void;
  onSavedViewChange: (viewId: string | null) => void;
  workspaceId: number;
};

type ObjectNameSearchProps = Pick<ObjectListTableProps, 'name' | 'objectType' | 'onSearch'>;

function ObjectNameSearch({ name, objectType, onSearch }: ObjectNameSearchProps) {
  const [value, setValue] = useState(name);
  const [previousName, setPreviousName] = useState(name);
  if (name !== previousName) {
    setPreviousName(name);
    setValue(name);
  }
  return (
    <Search
      className="fs-mask"
      ariaLabel={`${objectType.singular_name} name search`}
      placeholder={`Find by ${objectType.singular_name} name`}
      value={value}
      onSearchInput={setValue}
      onSearch={onSearch}
      shouldSearchOnEnter
      shouldSearchOnClear
    />
  );
}

type ObjectSearchSlotProps = Pick<
  ObjectListTableProps,
  'activeFilterBy' | 'filters' | 'name' | 'objectType' | 'onActivateFilterBy' | 'onApplyFilters' | 'onSearch'
>;

function ObjectSearchSlot({
  activeFilterBy,
  filters,
  name,
  objectType,
  onActivateFilterBy,
  onApplyFilters,
  onSearch,
}: ObjectSearchSlotProps) {
  return (
    <>
      <DataTableHeaderRow>
        <Flex alignItems="center" gap="150">
          <TextLabel id="objects-filter-by-label">Filter by</TextLabel>
          <SegmentedControl
            size="sm"
            name="toggleObjectFilterBy"
            ariaLabelledby="objects-filter-by-label"
            value={activeFilterBy}
            options={[
              { label: 'Name', value: 'name' },
              { label: 'Conditions', value: 'conditions' },
            ]}
            onChange={(value: string) => onActivateFilterBy(value === 'conditions' ? 'conditions' : 'name')}
          />
        </Flex>
      </DataTableHeaderRow>
      <DataTableHeaderRow overflow="visible">
        {activeFilterBy === 'name' ? (
          <ObjectNameSearch name={name} objectType={objectType} onSearch={onSearch} />
        ) : (
          <ObjectFilterBuilder
            filters={filters}
            objectTypeId={objectType.id}
            attributeNames={objectType.sent_attributes ?? []}
            onChange={onApplyFilters}
          />
        )}
      </DataTableHeaderRow>
    </>
  );
}

function ObjectListEmptyState({
  objectType,
  canConfigure,
  workspaceId,
}: Pick<ObjectListTableProps, 'objectType' | 'canConfigure' | 'workspaceId'>) {
  const router = useRouter();
  const guideHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/guide',
    params: { workspaceId, slug: objectType.slug },
  }).href;
  const newHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/new',
    params: { workspaceId, slug: objectType.slug },
  }).href;
  return (
    <EmptyState>
      <EmptyStateTitle icon={objectIcon(objectType.icon)}>You don't have any {objectType.name} yet</EmptyStateTitle>
      <EmptyStateDescription>
        View the setup guide to automatically create objects via the API. Or, manually create one to test how it works.
      </EmptyStateDescription>
      <EmptyStateActions>
        <Button variant="primary" href={guideHref}>
          View setup guide
        </Button>
        <Button variant="secondary" href={newHref} isDisabled={!canConfigure}>
          Add {objectType.singular_name}
        </Button>
      </EmptyStateActions>
    </EmptyState>
  );
}

type ObjectColumnsProps = Pick<
  ObjectListTableProps,
  'availableColumns' | 'objectType' | 'shouldRedactCreatedAt' | 'workspaceId'
>;

function useObjectColumns({ availableColumns, objectType, shouldRedactCreatedAt, workspaceId }: ObjectColumnsProps) {
  return useMemo<ColumnDef<DataTableFeatures, ObjectRecord>[]>(() => {
    const result: ColumnDef<DataTableFeatures, ObjectRecord>[] = [
      {
        id: 'name',
        header: 'name',
        meta: FIXED_COLUMN_META,
        enableHiding: false,
        cell: ({ row }) => {
          const name = objectName(row.original);
          const href = generatePath('/workspaces/:workspaceId/journeys/objects/:objectSlug/:objectId', {
            workspaceId,
            objectSlug: objectType.slug,
            objectId: row.original.id,
          });
          return name == null ? (
            <Text>—</Text>
          ) : (
            <Link className="fs-mask" variant="primary" href={href}>
              {name}
            </Link>
          );
        },
      },
      {
        id: 'created_at',
        header: 'created_at',
        meta: FIXED_COLUMN_META,
        enableHiding: false,
        cell: ({ row }) => {
          const createdAt = Number(row.original.attributes.created_at);
          return shouldRedactCreatedAt ? (
            <Text>*****</Text>
          ) : (
            <FormattedRelativeTime
              className="fs-mask"
              date={Number.isFinite(createdAt) && createdAt > 0 ? createdAt : undefined}
              shouldUppercaseFirst={false}
              withTooltip
            />
          );
        },
      },
    ];
    for (const attribute of availableColumns) {
      result.push({
        id: attribute,
        header: attribute,
        cell: ({ row }) => {
          const value = row.original.attributes[attribute] ?? '';
          return isTimestamp(value) ? (
            <FormattedDateTime className="fs-mask" date={Number(value)} withTooltip />
          ) : (
            <Truncated className="fs-mask">{value}</Truncated>
          );
        },
      });
    }
    return result;
  }, [availableColumns, objectType.slug, shouldRedactCreatedAt, workspaceId]);
}

// fallow-ignore-next-line complexity
export function ObjectListTable({
  objectType,
  objects,
  total,
  page,
  name,
  filters,
  activeFilterBy,
  withoutRelationships,
  withRelationships,
  message,
  isLoading,
  canConfigure,
  canDelete,
  shouldRedactCreatedAt,
  availableColumns,
  columnVisibility,
  onColumnVisibilityChange,
  columnsSettings,
  rowSelection,
  allSelected,
  allMatchingSelected,
  deleteFilters,
  onRowSelectionChange,
  onSelectAll,
  onClearSelection,
  onSearch,
  onApplyFilters,
  onActivateFilterBy,
  onPageChange,
  onSavedViewChange,
  workspaceId,
}: ObjectListTableProps) {
  const deleteModal = useDeleteObjectsModal();
  const columns = useObjectColumns({ availableColumns, objectType, shouldRedactCreatedAt, workspaceId });

  const hasFilters = !isEmptyString(name) || filters != null || withoutRelationships || withRelationships;
  const selected = objects.filter((object) => rowSelection[object.id] === true);
  const openDelete = () => {
    if (selected.length === 0 && !allSelected) return;
    const selection = allMatchingSelected
      ? deleteFilters != null
        ? { filters: deleteFilters, withoutRelationships: withoutRelationships || undefined }
        : { withoutRelationships: true as const }
      : { cioObjectIds: selected.map((object) => object.identifiers.cio_object_id) };
    const id = deleteModal.open(
      {
        objectTypeId: objectType.id,
        objectTypeName: objectType.singular_name,
        count: allMatchingSelected ? total : selected.length,
        selection,
      },
      { shouldCloseOnUnmount: true, withImmediateCallbacks: true },
    );
    void deleteModal.waitForClose(id).then((result) => {
      if (result.reason === 'confirm') onClearSelection();
    });
  };

  if (!isLoading && !hasFilters && objects.length === 0) {
    return <ObjectListEmptyState objectType={objectType} canConfigure={canConfigure} workspaceId={workspaceId} />;
  }

  const selectedCount = selected.length;
  const listCount = allSelected
    ? `${total === 1 ? '' : 'All '}${formatNumberAndPluralize(total, objectType.singular_name)} selected`
    : selectedCount > 0
      ? `${selectedCount} out of ${formatNumberAndPluralize(total, objectType.singular_name)} selected`
      : formatNumberAndPluralize(total, objectType.singular_name);
  const canSelectAllMatching =
    isEmptyString(name) &&
    (deleteFilters != null || (filters == null && withoutRelationships)) &&
    !withRelationships &&
    total >= 0;

  return (
    <>
      <Heading level="3" mb="300">
        {listCount}
      </Heading>
      <DataTable
        caption="Object list"
        data={objects}
        columns={columns}
        getRowId={(object) => object.id}
        isLoading={isLoading}
        withPagination
        pagination={{
          page,
          pageSize: 50,
          totalItemCount: total,
          onPageChange: (nextPage) => onPageChange(nextPage),
          isManual: true,
        }}
        withRowSelection={canConfigure}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        bulkActions={{
          promotedActions: [
            {
              label: 'Delete',
              isDanger: true,
              isDisabled: !canDelete || (selected.length === 0 && !allSelected),
              tooltip: canDelete ? undefined : 'You do not have permission to delete objects.',
              onClick: openDelete,
            },
          ],
        }}
        withSavedViews
        savedViews={{
          defaultViewName: `All ${objectType.name}`,
          activeViewId: withoutRelationships
            ? 'objects_without_relationships'
            : withRelationships
              ? 'objects_with_relationships'
              : null,
          onActiveViewChange: (viewId) => onSavedViewChange(viewId),
          views: [
            { id: 'objects_without_relationships', name: `${objectType.name} without relationships` },
            { id: 'objects_with_relationships', name: `${objectType.name} with relationships` },
          ],
        }}
        withColumnsSettings
        columnsSettings={columnsSettings}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        emptyState={hasFilters ? { title: 'No objects matching your search', icon: 'object' } : undefined}
      >
        <Panel>
          <DataTableHeader>
            <ObjectSearchSlot
              objectType={objectType}
              name={name}
              filters={filters}
              activeFilterBy={activeFilterBy}
              onSearch={onSearch}
              onApplyFilters={onApplyFilters}
              onActivateFilterBy={onActivateFilterBy}
            />
            {!isEmptyString(message) ? <Banner variant="caution" description={message} /> : null}
            <DataTableHeaderRow>
              <DataTableHeaderActions>
                <DataTableHeaderActionsLeftSection>
                  <DataTableHeaderSavedViews />
                </DataTableHeaderActionsLeftSection>
                <DataTableHeaderActionsRightSection>
                  <DataTableHeaderColumnsMenu label="Edit displayed attributes" isDisabled={!canConfigure} />
                  {canSelectAllMatching && objects.length > 0 && !allSelected ? (
                    <Button size="sm" onClick={onSelectAll}>
                      Select {total > 1 ? 'all ' : ''}
                      {formatNumberAndPluralize(total, objectType.singular_name)}
                    </Button>
                  ) : null}
                  {allSelected || selected.length > 0 ? (
                    <Button size="sm" onClick={onClearSelection}>
                      Clear selection
                    </Button>
                  ) : null}
                  <DataTablePagination mt="0" />
                </DataTableHeaderActionsRightSection>
              </DataTableHeaderActions>
            </DataTableHeaderRow>
          </DataTableHeader>
          <PanelInset>
            <DataTableTable />
          </PanelInset>
        </Panel>
        <DataTablePagination mt="200" />
      </DataTable>
    </>
  );
}
